import { useState } from "react";
import { CLIENTS, SEGMENT_LABEL, STATUS_LABEL, type Client } from "../data/clients";
import { useTasks } from "../store/tasks";
import { useUI } from "../store/ui";
import { useClientBrand, effectiveBrand } from "../store/clientBrand";
import { BrandKitEditor } from "./BrandKitEditor";

const STATUS_COLOR: Record<Client["status"], string> = {
  ativo:      "bg-ink text-bone",
  onboarding: "bg-gold text-ink",
  pausado:    "bg-graydk text-bone",
  prospect:   "bg-red text-bone"
};

const PRIORITY_COLOR: Record<Client["priority"], string> = {
  core:      "bg-red text-bone",
  growth:    "bg-gold text-ink",
  satellite: "bg-graydk text-bone"
};

export function ClientsView() {
  const tasks      = useTasks(s => s.tasks);
  const setView    = useUI(s => s.setView);
  const overrides  = useClientBrand(s => s.overrides);
  const [kind, setKind]     = useState<"client" | "tool">("client");
  const [filter, setFilter] = useState<"all" | Client["status"]>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const inKind = CLIENTS.filter(c => (c.kind ?? "client") === kind);
  const visible = filter === "all" ? inKind : inKind.filter(c => c.status === filter);
  const totalFee = inKind.filter(c => c.status === "ativo").reduce((a, c) => a + c.fee, 0);

  const tasksByClient = (clientId: string) =>
    tasks.filter(t => t.clientId === clientId);

  return (
    <div className="px-8 pb-16">
      <header className={`border-2 border-ink text-bone px-5 py-4 mb-4
        ${kind === "client" ? "bg-ink" : "bg-red"}`}>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-xs text-gold">{kind === "client" ? "CLI-01" : "TOL-01"}</div>
            <h2 className="font-display font-black uppercase text-4xl leading-none">
              {kind === "client" ? "PORTFÓLIO DE CLIENTES" : "FERRAMENTAS INTERNAS"}
            </h2>
            <div className="font-serif italic mt-1 text-bone/70">
              {inKind.length} {kind === "client" ? "contas" : "ferramentas"} ·{" "}
              {kind === "client"
                ? <>USD {totalFee.toLocaleString("en-US")}/mês recorrente</>
                : "ativos da casa — equipe pode usar/alterar livremente"}
            </div>
          </div>
          <div className="flex border-2 border-bone">
            {(["client","tool"] as const).map(k => (
              <button key={k} onClick={() => setKind(k)}
                className={`tag px-4 py-1.5 border-r last:border-r-0 border-bone
                  ${kind === k ? "bg-bone text-ink" : "text-bone hover:bg-bone/10"}`}>
                {k === "client" ? "Clientes externos" : "Ferramentas internas"}
              </button>
            ))}
          </div>
        </div>
        <div className="flex border-2 border-bone mt-3">
          {(["all","ativo","onboarding","prospect","pausado"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`tag px-3 py-1.5 border-r last:border-r-0 border-bone
                ${filter === f ? "bg-bone text-ink" : "text-bone hover:bg-bone/10"}`}>
              {f === "all" ? "todos" : f}
            </button>
          ))}
        </div>
      </header>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {visible.map(c => {
          const tc = tasksByClient(c.id);
          const open = c.id === openId;
          const eff = effectiveBrand(c, overrides[c.id]);
          const logo = eff.logoUrl;
          const spokes = eff.spokespersonUrl;
          return (
            <article key={c.id} className="border-2 border-ink bg-bone overflow-hidden">
              {/* brand band */}
              <div className="p-4" style={{ background: eff.primary, color: eff.accent }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] opacity-70">{c.id.toUpperCase()}</span>
                  <span className={`tag px-1.5 ${PRIORITY_COLOR[c.priority]}`}>{c.priority}</span>
                </div>
                <div className="flex items-start gap-3">
                  {logo && (
                    <div className="shrink-0 w-16 h-16 bg-bone p-1 flex items-center justify-center">
                      <img src={logo} alt="" className="max-w-full max-h-full object-contain" />
                    </div>
                  )}
                  {spokes && (
                    <div className="shrink-0 w-16 h-16 border-2 border-bone overflow-hidden">
                      <img src={spokes} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-black uppercase text-2xl leading-none">{c.shortName}</h3>
                    <div className="font-crimson italic text-sm opacity-80 mt-1">{SEGMENT_LABEL[c.segment]}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className={`tag px-2 py-0.5 ${STATUS_COLOR[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                  <span className="tag" style={{ background: eff.secondary, color: eff.primary }}>
                    USD {c.fee.toLocaleString("en-US")}/mês
                  </span>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <p className="font-crimson italic text-paper text-sm leading-snug">{c.pitch}</p>

                {/* QUICK LINKS — visíveis em tudo, mas obrigatórios em ferramentas */}
                {(c.url || c.githubUrl || c.claudePlaygroundUrl) && (
                  <div className="flex flex-wrap gap-1">
                    {c.url && (
                      <a href={c.url.startsWith("http") ? c.url : `https://${c.url}`}
                         target="_blank" rel="noreferrer"
                         className="tag bg-ink text-bone px-2 py-1 hover:bg-red">
                        ↗ abrir app
                      </a>
                    )}
                    {c.githubUrl && (
                      <a href={c.githubUrl} target="_blank" rel="noreferrer"
                         className="tag bg-paper text-bone px-2 py-1 hover:bg-ink">
                        ⌬ github
                      </a>
                    )}
                    {c.claudePlaygroundUrl && (
                      <a href={c.claudePlaygroundUrl} target="_blank" rel="noreferrer"
                         className="tag bg-gold text-ink px-2 py-1 hover:bg-red hover:text-bone">
                        ✦ claude playground
                      </a>
                    )}
                    {c.kind === "tool" && !c.url && (
                      <a href="https://claude.ai/code" target="_blank" rel="noreferrer"
                         className="tag bg-red text-bone px-2 py-1 hover:bg-ink">
                        + criar com claude
                      </a>
                    )}
                  </div>
                )}

                <div>
                  <div className="tag text-graydk">público-alvo</div>
                  <p className="font-mono text-[12px] text-paper">{c.audience}</p>
                </div>

                {open && (
                  <>
                    <button onClick={() => setEditId(c.id)}
                      className="btn bg-ink text-bone border-ink w-full">
                      ✦ abrir brand kit completo
                    </button>

                    <div>
                      <div className="tag text-graydk">desafio principal</div>
                      <p className="font-mono text-[12px] text-paper">{c.challenge}</p>
                    </div>
                    <div>
                      <div className="tag text-graydk">tags</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.tags.map(t => (
                          <span key={t} className="tag bg-ink/10 text-ink px-1.5">{t}</span>
                        ))}
                      </div>
                    </div>
                    {c.url && (
                      <div>
                        <div className="tag text-graydk">site</div>
                        <span className="font-mono text-[12px]">{c.url}</span>
                      </div>
                    )}
                    <div>
                      <div className="tag text-graydk">desde</div>
                      <span className="font-mono text-[12px]">{c.since}</span>
                    </div>
                    <div>
                      <div className="tag text-graydk mb-1">brand kit</div>
                      <div className="flex gap-1">
                        <Swatch color={eff.primary}   label="primary" />
                        <Swatch color={eff.secondary} label="secondary" />
                        <Swatch color={eff.accent}    label="accent" />
                      </div>
                      {eff.font && <div className="tag text-graydk mt-1">font: {eff.font}</div>}
                      {eff.persona && (
                        <div className="mt-2">
                          <div className="tag text-graydk">persona</div>
                          <p className="font-crimson italic text-paper text-[12px]">{eff.persona}</p>
                        </div>
                      )}
                      {eff.voiceTone && (
                        <div className="mt-2">
                          <div className="tag text-graydk">tom de voz</div>
                          <p className="font-crimson italic text-paper text-[12px]">{eff.voiceTone}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-ink/20">
                  <div className="tag text-graydk">
                    {tc.length} demanda{tc.length === 1 ? "" : "s"} ·{" "}
                    {tc.filter(t => t.status !== "done").length} ativa{tc.filter(t => t.status !== "done").length === 1 ? "" : "s"}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setOpenId(open ? null : c.id)} className="btn btn-fire">
                      {open ? "fechar" : "ver mais"}
                    </button>
                    <button onClick={() => setView("demands")} className="btn btn-fire">
                      demandas →
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {editId && <BrandKitEditor clientId={editId} onClose={() => setEditId(null)} />}
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 border-2 border-ink" style={{ background: color }} />
      <div className="tag text-graydk text-[9px]">{label}</div>
      <div className="font-mono text-[9px]">{color}</div>
    </div>
  );
}

